import React, {SyntheticEvent, useEffect, useState} from "react";
import {Box, FormLabel, IconButton, Typography, useTheme} from "@mui/material";
import Person from "../../../model/Person";
import PersonCard from "./PersonCard";
import Model from "../../../model/Model";
import {createUUID, getISODateToday} from "../../../stores/StoreUtilities";
import TimelineChart from "./charts/TimelineChart";
import Plan from "../../../model/Plan";
import {useResources} from "../../../stores/ResourceProvider";
import UserSetting, {IQuickStartSettings, UserSettingType} from "../../../model/UserSetting";
import {UpdateUserSettingInput} from "../../../API";
import RoutesConfig from "../../../RoutesConfig";
import {useNavigate} from "react-router-dom";
import TimelineQuickStart from "./TimelineQuickStart";
import useIsMobile from "../../../components/useIsMobile";
import MobileDrawerButton from "../../../components/controls/MobileDrawerButton";
import Section from "../../../components/controls/Section";
import {AddCircleRounded} from "@mui/icons-material";
import Milestone from "../../../model/Milestone";
import MilestoneCard from "./MilestoneCard";

enum TimelineChartView {
  Timeline,
  QuickStart
}

interface ITimelineViewSettings {
  expandedSections: string[]
}

export enum TimelineSection {
  People = "People",
  Milestones = "Milestones"
}

const TimelineView = ({
  model,
  plan,
  updatedAt,
  onChange,
  width,
  toggleDrawer
} : {
  model: Model
  plan: Plan
  updatedAt: string
  onChange?(model: Model): void
  width: number
  toggleDrawer: () => void
}) => {
  const [persons, setPersons] = useState<Person[]>([])
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [chartView, setChartView] = useState<TimelineChartView>(TimelineChartView.Timeline)

  const [quickStartUserSetting, setQuickStartUserSetting] = useState<UserSetting | undefined>()
  const [quickStartStep, setQuickStartStep] = useState<string | undefined>()
  const [showQuickStart, setShowQuickStart] = useState<boolean>(true)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [viewStorageKey, setViewStorageKey] = useState<string>("")
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set<string>([]))

  const {userStore, modelStore} = useResources()
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    }
  }

  useEffect(() => {
    if (model) {
      setPersons(model.persons.filter((p: Person) => p.id !== model.jointId))
      setMilestones(Array.from(model.milestones.values())
        .filter((m: Milestone) => m.isCustom)
        .sort((a: Milestone, b: Milestone) => (a.date ?? "").localeCompare(b.date ?? "")))
      setIsLocked(model.isLocked())
      const userSetting = userStore!.user!.getUserSetting(UserSettingType.quickStart, model.id)
      if (userSetting) {
        const quickStartSettings: IQuickStartSettings = userSetting.settings
        if (quickStartSettings && (!quickStartSettings.status || quickStartSettings.status === "Active") && quickStartSettings.view === "timeline") {
          setShowQuickStart(true)
          setQuickStartUserSetting(userSetting)
          setQuickStartStep(quickStartSettings.step)
          setChartView(TimelineChartView.QuickStart)
        }
      }
    }

    // Load localStorage view settings
    loadSettings()
  }, [model, plan, updatedAt])

  useEffect(() => {
    if (model) {
      saveSettings()
    }
  }, [expandedSections, chartView, viewStorageKey])

  const loadSettings = () => {
    let storageKey = viewStorageKey
    if (!storageKey) {
      storageKey = `${modelStore.currentUser?.id}TimelineViewSettings`
      setViewStorageKey(storageKey)
    }
    const data = window.localStorage.getItem(storageKey)
    if (data) {
      const settings: ITimelineViewSettings = JSON.parse(data)
      if (settings.expandedSections && settings.expandedSections.length && typeof settings.expandedSections[0] === "string") {
        setExpandedSections(new Set<string>([...settings.expandedSections]))
      } else {
        setAllSectionsExpanded(true)
      }
    } else {
      setAllSectionsExpanded(true)
    }
  }

  const saveSettings = () => {
    const settings = JSON.stringify({
      expandedSections: Array.from(expandedSections)
    })
    window.localStorage.setItem(viewStorageKey, settings)
  }


  const getExpandedSection = (id: TimelineSection) => {
    return expandedSections.has(id)
  }

  const setExpandedSection = (id: TimelineSection, expand: boolean) => {
    if (expand) {
      expandedSections.add(id)
    } else {
      expandedSections.delete(id)
    }
    setExpandedSections(new Set(expandedSections))
  }

  const setAllSectionsExpanded = (expanded: boolean) => {
    if (expanded) {
      setExpandedSections(new Set<string>(Object.values(TimelineSection)))
    } else {
      setExpandedSections(new Set<string>([]))
    }
  }

  const handleAddPerson = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (model) {
      const newPerson = new Person({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        nickname: `Person ${model.persons.length + 1}`,
      })
      setPersons([...persons, newPerson])
      setExpandedSection(TimelineSection.People, true)
    }
  }

  const handleSavePerson = async (person: Person) => {
    const index = model.persons.findIndex((p: Person) => p.id === person.id)
    if (index >= 0) {
      model.persons[index] = person
    } else {
      model.persons.push(person)
    }
    setPersons(model.persons.filter((p: Person) => p.id !== model.jointId))
    if (onChange) {
      onChange(model)
    }
  }

  const handleDeletePerson = (deleted: Person) => {
    const index = persons.findIndex((p: Person) => p.id === deleted.id)
    if (index >= 0) {
      persons.splice(index, 1)
    }
    setPersons([...persons])
    if (onChange) {
      onChange(model)
    }
  }

  const handleAddMilestone = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const milestone = new Milestone({
      id: createUUID(),
      accountId: model.accountId,
      userId: model.userId,
      modelId: model.id,
      label: "New Milestone",
      date: getISODateToday(),
      description: ""
    })
    setMilestones([...milestones, milestone])
    setExpandedSection(TimelineSection.Milestones, true)
  }

  const handleSaveMilestone = async (milestone: Milestone) => {
    const index = milestones.findIndex((m: Milestone) => m.id === milestone.id)
    if (index >= 0) {
      milestones[index] = milestone
      setMilestones([...milestones])
    }

    if (onChange) {
      onChange(model)
    }
  }

  const handleDeleteMilestone = (deleted: Milestone) => {
    const index = milestones.findIndex((m: Milestone) => m.id === deleted.id)
    if (index >= 0) {
      milestones.splice(index, 1)
    }
    setMilestones([...milestones])
    if (onChange) {
      onChange(model)
    }
  }

  const handleNext = (settings: IQuickStartSettings) => {
    const {view} = settings

    if (quickStartUserSetting) {
      const input: UpdateUserSettingInput = {
        id: quickStartUserSetting.id,
        settings: JSON.stringify(settings)
      }
      userStore.updateUserSetting(input)
        .then((userSetting?: UserSetting) => {
          if (userSetting) {
            if (view === "networth") {
              setQuickStartUserSetting(undefined)
              setQuickStartStep(undefined)
              setChartView(TimelineChartView.Timeline)
              setShowQuickStart(false)
              navigate(`${RoutesConfig.calc.pathname}/${view}`)
            } else if (view === "timeline") {
              setQuickStartUserSetting(userSetting)
              setQuickStartStep(userSetting.settings["step"])
            }
          }
        })
    }
  }

  const handleExit = async(settings: IQuickStartSettings) => {
    if (quickStartUserSetting) {
      if (settings.status === "Exited" && userStore.isAdvisorOrAdmin) {
        await userStore.deleteUserSetting(quickStartUserSetting.id)
      } else {
        const input: UpdateUserSettingInput = {
          id: quickStartUserSetting.id,
          settings: JSON.stringify(settings)
        }
        await userStore.updateUserSetting(input)
      }
      setQuickStartUserSetting(undefined)
      setQuickStartStep(undefined)
      setShowQuickStart(false)
      setChartView(TimelineChartView.Timeline)
    }
  }


  return (
    <Box sx={{display:"flex", flexDirection:"row", mt:1, minHeight: "calc(100vh - 150px)"}}>
      <Box sx={{pl:0, width: { xs:width-8, sm:360}, minHeight:"100%"}}>
        <Box sx={{display:"flex", mt:0, pl:0, pb:0, mb:"2px", justifyContent:"stretch", alignItems:"center"}}>
          {isMobile && <MobileDrawerButton onClick={toggleDrawer}/>}
          <Typography variant="h2" color="primary">Timeline</Typography>
        </Box>
        <Box display="flex" flexDirection="column">
          {isMobile &&
            <Box sx={{display:"flex", flexDirection:"column", width:width-8, alignItems:"center", mt: 1,mb:1}}>
              <TimelineChart persons={persons} view="chart"/>
            </Box>
          }
          <Section title="Persons" total={Math.min(persons.length, 2).toString()}
                   expanded={getExpandedSection(TimelineSection.People)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(TimelineSection.People, value)}
                   button={(persons.length < 2 && !isLocked) ?
                     <Box display="wrap" flexWrap="nowrap" pt={0} justifyContent="flex-end">
                       <IconButton sx={{height:28, width:28}} onClick={handleAddPerson}>
                         <AddCircleRounded sx={{color: theme.palette.secondary.light}}/>
                       </IconButton>
                     </Box>: undefined
                   }
          >
            {persons.map((person: Person, index: number) =>
              <PersonCard person={person} model={model} key={`person-${index}`}
                          onSave={handleSavePerson}
                          onDelete={handleDeletePerson}/>
            )}
          </Section>

          <Section title="Custom Milestones" total={milestones.length.toString()}
                   expanded={getExpandedSection(TimelineSection.Milestones)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(TimelineSection.Milestones, value)}
                   button={
                     <IconButton sx={{height:28, width:28}} onClick={handleAddMilestone}>
                       <AddCircleRounded sx={{color: theme.palette.secondary.light}}/>
                     </IconButton>
                   }
          >
            {milestones.map((milestone: Milestone, index:number) =>
              <MilestoneCard milestone={milestone} model={model} key={milestone.id}
                          onSave={handleSaveMilestone}
                          onDelete={handleDeleteMilestone}
              />
            )}
          </Section>

          {isMobile &&
            <Box sx={{display:"flex", flexDirection:"column", width:width-8, alignItems:"center", mt: 1,mb:1}}>
              <TimelineChart persons={persons} view="table"/>
            </Box>
          }
        </Box>

      </Box>
      {!isMobile &&
        <Box width="100%" pl={2} py={0} mt={0}>
          {chartView === TimelineChartView.QuickStart &&
            <TimelineQuickStart step={quickStartStep}
                                model={model}
                                quickStartUserSetting={quickStartUserSetting}
                                onSave={(item: Person) => handleSavePerson(item)}
                                onNext={handleNext}
                                onExit={handleExit}
            />
          }

          {chartView === TimelineChartView.Timeline &&
            <>
              <Box sx={{display:"flex", flexGrow:1, flexDirection:"row", justifyContent:"stretch", paddingLeft: 0, paddingRight: 0.5, maxHeight: 50}}>
                <Box sx={{display:"flex", flexGrow:2, flexDirection:"column", minWidth:250}}>
                  <Box sx={{display:"flex", flexGrow:1, justifyContent:"space-between", maxHeight:16}}>
                    <FormLabel sx={styles.formLabel}>&nbsp;</FormLabel>
                  </Box>
                  <Box sx={{display:"flex", flexGrow:1, flexWrap:"nowrap", marginBottom:"4px", pl:1}}>
                    <Typography variant="h3" color="primary" gutterBottom>Retirement Timeline</Typography>
                  </Box>
                </Box>
              </Box>
              <TimelineChart persons={persons} view="all"/>
            </>
          }
        </Box>
      }
    </Box>
  )
}

export default TimelineView